// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import CartPageTemplate, {
    cartPageTemplateStyles,
    CartPageTemplateStyles,
} from "@werner/App/Components/Cart/CartPageTemplate";
import XngZone from "@werner/App/Components/Common/XngZone";
import GridContainer, { GridContainerProps } from "@werner/MobiusOverrides/GridContainer/GridContainer";
import { DivWrapper } from "@werner/XDL/constants";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = WidgetProps;

interface OrderStatusLayoutStyles {
    wrapper?: GridContainerProps;
    cartPageTemplateStyles?: CartPageTemplateStyles;
    cartPageTemplateGridItem?: GridItemProps;
    orderStatusPageTitleGridItem?: GridItemProps;
    orderStatusInstructionsGridItem?: GridItemProps;
    orderStatusFormGridItem?: GridItemProps;
    orderStatusMainSectionGridContainer?: GridContainerProps;
    orderStatusInformationGridItem?: GridItemProps;
    orderStatusAddressDetailsGridItem?: GridItemProps;
    orderStatusShipmentPackagesGridItem?: GridItemProps;
}

const orderStatusLayoutStyles: OrderStatusLayoutStyles = {
    wrapper: {
        gap: 0,
        css: css`
            ${GridItemStyle}:empty {
                display: none;
            }
        `,
    },
    cartPageTemplateGridItem: {
        width: 12,
    },
    orderStatusPageTitleGridItem: {
        width: 12,
    },
    orderStatusInstructionsGridItem: {
        width: 12,
        css: css`
            &&& {
                margin-bottom: 16px;
            }
        `,
    },
    orderStatusFormGridItem: {
        width: 12,
        css: css`
            &&& {
                margin-bottom: 32px;
            }
        `,
    },
    orderStatusMainSectionGridContainer: {
        gap: 16,
        offsetCss: css`
            > ${GridItemStyle}:has(> ${DivWrapper}:empty) {
                display: none;
            }
        `,
    },
    orderStatusInformationGridItem: {
        width: [12, 12, 6, 4, 4],
    },
    orderStatusAddressDetailsGridItem: {
        width: [12, 12, 6, 8, 8],
    },
    orderStatusShipmentPackagesGridItem: {
        width: 12,
    },
    cartPageTemplateStyles: {
        ...cartPageTemplateStyles,
        totalSection: {
            css: css`
                ${cartPageTemplateStyles.totalSection?.css}
                &:has(> div.xng-sticky > ${DivWrapper}:empty) {
                    display: none;
                }
            `,
        },
    },
};

const styles = orderStatusLayoutStyles;
const OrderStatusLayout: FC<Props> = ({ id }) => {
    return (
        <GridContainer {...styles.wrapper}>
            <GridItem {...styles.orderStatusPageTitleGridItem}>
                <XngZone hint="Zone For Page Title" contentId={id} zoneName="OrderStatusPageTitle" />
            </GridItem>
            <GridItem {...styles.orderStatusInstructionsGridItem}>
                <XngZone hint="Zone For Instructions" contentId={id} zoneName="OrderStatusInstructions" />
            </GridItem>
            <GridItem {...styles.orderStatusFormGridItem}>
                <XngZone hint="Zone For Order Status Form" contentId={id} zoneName="OrderStatusForm" />
            </GridItem>
            <GridItem {...styles.cartPageTemplateGridItem}>
                <CartPageTemplate extendedStyles={styles.cartPageTemplateStyles}>
                    <CartPageTemplate.HeaderSection>
                        <XngZone
                            hint="Zone For Order Details Header Title"
                            contentId={id}
                            zoneName="OrderDetailsHeaderTitle"
                        />
                    </CartPageTemplate.HeaderSection>
                    <CartPageTemplate.MainSection>
                        <GridContainer {...styles.orderStatusMainSectionGridContainer}>
                            <GridItem {...styles.orderStatusInformationGridItem}>
                                <XngZone
                                    hint="Zone For Order Status Information"
                                    contentId={id}
                                    zoneName="OrderStatusInformation"
                                />
                            </GridItem>

                            <GridItem {...styles.orderStatusAddressDetailsGridItem}>
                                <XngZone
                                    hint="Zone For Order Status Address Details"
                                    contentId={id}
                                    zoneName="OrderStatusAddressDetails"
                                />
                            </GridItem>

                            <GridItem {...styles.orderStatusShipmentPackagesGridItem}>
                                <XngZone
                                    hint="Zone For Order Status Shipment Packages"
                                    contentId={id}
                                    zoneName="OrderStatusShipmentPackages"
                                />
                            </GridItem>
                        </GridContainer>
                        <XngZone
                            hint="Zone For Order Status Product List"
                            contentId={id}
                            zoneName="OrderStatusProductList"
                        />
                    </CartPageTemplate.MainSection>
                    <CartPageTemplate.CartTotalSection>
                        <XngZone
                            hint="Zone For Order Details Cart Total"
                            contentId={id}
                            zoneName="OrderDetailsCartTotal"
                        />
                    </CartPageTemplate.CartTotalSection>
                </CartPageTemplate>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: OrderStatusLayout,
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Order Status Layout",
    },
};

export default widgetModule;
